import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  Zap, Settings, PlayCircle, AlertTriangle, CheckCircle2,
  ArrowLeft, Wrench, User, Package, Layers, Loader
} from "lucide-react";

/**
 * ClassifyStrokes — the page a user lands on after clicking the yellow SETUP
 * badge or red unmapped-strokes banner. Presents two big paths:
 *   (1) Log as Setup Stroke — reason + optional remark
 *   (2) Start Production Run — operator + tool + material (RM) batch
 */
const ClassifyStrokes = () => {
  const { machineCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // "setup" | "production"
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [reasonCodes, setReasonCodes] = useState([]);
  const [setupForm, setSetupForm] = useState({ reason_code: "tool_setting", remark: "" });

  const [operators, setOperators] = useState([]);
  const [tools, setTools] = useState([]);
  const [batches, setBatches] = useState([]);
  const [opInfo, setOpInfo] = useState(null);
  const [prodForm, setProdForm] = useState({
    operator: "",
    tool: "",
    batch: "",
    subCode: "A",
    batchNotAvailable: false,
  });

  const fetchCtx = () => {
    setLoading(true);
    api.get(`/machine-setup-strokes/${machineCode}/context`)
      .then((r) => setCtx(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCtx();
    api.get("/machine-setup-strokes/reason-codes").then((r) => setReasonCodes(r.data || []));
    api.get("/operators").then((r) => setOperators(r.data || []));
    api.get("/tools").then((r) => setTools(r.data || []));
  }, [machineCode]);

  // When a tool is selected, refresh op-info + batches (for first-op tools).
  useEffect(() => {
    if (!prodForm.tool) {
      setOpInfo(null);
      setBatches([]);
      return;
    }
    const t = tools.find((x) => x.tool_no === prodForm.tool);
    if (!t) return;
    api.get(`/production/tool-precheck?tool_no=${t.tool_no}`)
      .then((r) => setOpInfo(r.data))
      .catch(() => setOpInfo(null));
    if (t.rm_grade) {
      api.get(`/rm-batches?grade=${t.rm_grade}&thickness=${t.thickness_mm}&width=${t.width_mm}`)
        .then((r) => setBatches(r.data || []))
        .catch(() => setBatches([]));
    }
  }, [prodForm.tool, tools]);

  const handleClassifySetup = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post(`/machine-setup-strokes/${machineCode}/classify`, {
        reason_code: setupForm.reason_code,
        remark: setupForm.remark,
      });
      setSuccess({
        title: "Setup strokes logged",
        detail: `${res.data.strokes} stroke(s) tagged as "${res.data.reason_label}"`,
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to log setup strokes");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartProduction = async () => {
    setSubmitting(true);
    setError("");
    try {
      const op = operators.find((o) => o.user_id === prodForm.operator);
      const t = tools.find((x) => x.tool_no === prodForm.tool);
      if (!op || !t) {
        setError("Please pick operator and tool");
        setSubmitting(false);
        return;
      }
      const isFirstOp = opInfo?.is_first_op !== false;
      const payload = {
        operator_name: op.full_name,
        operator_id: op.user_id,
        machine_code: machineCode,
        tool_no: t.tool_no,
        batch_code: isFirstOp && !prodForm.batchNotAvailable ? prodForm.batch : "",
        sub_code: prodForm.subCode || "A",
        manual_batch: isFirstOp && prodForm.batchNotAvailable,
      };
      const res = await api.post("/production/stage1", payload);
      const rolled = res.data.rolled_in_setup_strokes || 0;
      setSuccess({
        title: "Production started",
        detail: rolled > 0
          ? `Job ${res.data.job_id} opened on ${machineCode} · ${rolled} pre-mapping stroke(s) rolled in`
          : `Job ${res.data.job_id} opened on ${machineCode}`,
        jobId: res.data.job_id,
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start production");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="p-8 text-center text-slate-400">Loading…</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto p-8">
          <div className="bg-white rounded-2xl shadow-lg border border-emerald-200 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-emerald-600" size={32} />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900">{success.title}</h2>
            <p className="text-slate-500 mt-2">{success.detail}</p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                data-testid="classify-back-dashboard"
                onClick={() => navigate("/dashboard")}
                className="bg-[#1A3560] hover:bg-[#152a4d] text-white px-4 py-2 rounded-lg font-bold text-sm"
              >
                Back to Dashboard
              </button>
              {success.jobId && (
                <button
                  onClick={() => navigate(`/production/update/${success.jobId}`)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-bold text-sm"
                >
                  Open Job {success.jobId}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        {/* Header card */}
        <div className="bg-gradient-to-r from-amber-500 to-red-500 text-white rounded-2xl p-6 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-3 animate-pulse">
                <Zap size={28} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest opacity-80">
                  Classify Strokes
                </p>
                <h1 className="text-3xl font-extrabold">{machineCode}</h1>
                <p className="text-sm opacity-90">
                  {ctx?.tonnage}T Press · {ctx?.setup_strokes || 0} unmapped stroke(s) captured
                </p>
              </div>
            </div>
            <div className="text-right text-xs opacity-90 hidden sm:block">
              <p>Last stroke</p>
              <p className="font-mono">
                {ctx?.last_stroke_at ? new Date(ctx.last_stroke_at).toLocaleString("en-IN") : "—"}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {ctx?.has_open_job && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4 flex items-center gap-2">
            <AlertTriangle size={16} />
            <span>
              <b>Note:</b> {machineCode} already has an OPEN job ({ctx.open_job?.job_id} — Tool {ctx.open_job?.tool_no}).
              Setup strokes on this machine will always route into the open job. To start a new production run, close the current job first.
            </span>
          </div>
        )}

        {/* Two big mode cards */}
        {!mode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              data-testid="mode-setup"
              onClick={() => setMode("setup")}
              disabled={!ctx?.setup_strokes}
              className="text-left bg-white hover:bg-amber-50 border-2 border-amber-200 hover:border-amber-500 rounded-2xl p-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Settings className="text-amber-700" size={26} />
              </div>
              <h2 className="text-xl font-extrabold text-slate-900 mb-1">Log as Setup Stroke</h2>
              <p className="text-sm text-slate-500">
                These strokes were before tool setting, during maintenance, or a test run — not real production.
              </p>
              {!ctx?.setup_strokes && (
                <p className="text-xs text-amber-700 mt-2 font-bold">
                  No unmapped strokes to log.
                </p>
              )}
            </button>

            <button
              data-testid="mode-production"
              onClick={() => setMode("production")}
              disabled={ctx?.has_open_job}
              className="text-left bg-white hover:bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-500 rounded-2xl p-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <PlayCircle className="text-emerald-700" size={26} />
              </div>
              <h2 className="text-xl font-extrabold text-slate-900 mb-1">Start Production Run</h2>
              <p className="text-sm text-slate-500">
                Map operator, tool and material batch — new strokes will count from 0 on this job.
              </p>
              {ctx?.has_open_job && (
                <p className="text-xs text-red-700 mt-2 font-bold">
                  A job is already open on this machine.
                </p>
              )}
            </button>
          </div>
        )}

        {/* Setup Stroke Form */}
        {mode === "setup" && (
          <div className="bg-white rounded-2xl shadow-lg border border-amber-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <Settings size={20} className="text-amber-700" /> Log Setup Strokes
              </h2>
              <button
                onClick={() => setMode(null)}
                className="text-xs text-slate-400 hover:text-slate-700"
              >
                Change choice
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
              You are about to record <b>{ctx?.setup_strokes || 0}</b> stroke(s) on <b>{machineCode}</b> as setup — this will reset the unmapped-strokes counter.
            </div>

            <label className="block text-xs font-bold text-slate-700 mb-3">
              Reason
              <select
                data-testid="setup-reason"
                value={setupForm.reason_code}
                onChange={(e) => setSetupForm({ ...setupForm, reason_code: e.target.value })}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500"
              >
                {reasonCodes.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </label>

            <label className="block text-xs font-bold text-slate-700 mb-4">
              Remark (optional)
              <textarea
                data-testid="setup-remark"
                value={setupForm.remark}
                onChange={(e) => setSetupForm({ ...setupForm, remark: e.target.value })}
                rows={3}
                placeholder="e.g. Die alignment before Job J20260805-005"
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500"
              />
            </label>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setMode(null)}
                className="text-sm font-bold text-slate-600 px-4 py-2 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                data-testid="setup-submit"
                onClick={handleClassifySetup}
                disabled={submitting || !ctx?.setup_strokes}
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white font-extrabold text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2"
              >
                {submitting ? <Loader className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                {submitting ? "Saving…" : `Log ${ctx?.setup_strokes || 0} setup stroke(s)`}
              </button>
            </div>
          </div>
        )}

        {/* Production Start Form */}
        {mode === "production" && (
          <div className="bg-white rounded-2xl shadow-lg border border-emerald-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <PlayCircle size={20} className="text-emerald-700" /> Start Production Run
              </h2>
              <button
                onClick={() => setMode(null)}
                className="text-xs text-slate-400 hover:text-slate-700"
              >
                Change choice
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block text-xs font-bold text-slate-700">
                <span className="inline-flex items-center gap-1"><User size={12} /> Operator</span>
                <select
                  data-testid="prod-operator"
                  value={prodForm.operator}
                  onChange={(e) => setProdForm({ ...prodForm, operator: e.target.value })}
                  className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">— Select operator —</option>
                  {operators.map((o) => (
                    <option key={o.user_id} value={o.user_id}>
                      {o.full_name} ({o.user_id})
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-xs font-bold text-slate-700">
                <span className="inline-flex items-center gap-1"><Wrench size={12} /> Tool</span>
                <select
                  data-testid="prod-tool"
                  value={prodForm.tool}
                  onChange={(e) => setProdForm({ ...prodForm, tool: e.target.value, batch: "", batchNotAvailable: false })}
                  className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">— Select tool —</option>
                  {tools.map((t) => (
                    <option key={t.tool_no} value={t.tool_no}>
                      T{t.tool_no} · {t.part_name} ({t.min_tonnage_t}T)
                    </option>
                  ))}
                </select>
              </label>

              {opInfo?.is_first_op !== false && (
                <label className="block text-xs font-bold text-slate-700 sm:col-span-2">
                  <span className="inline-flex items-center gap-1">
                    <Package size={12} /> RM Batch (Material Code)
                  </span>
                  {batches.length > 0 && !prodForm.batchNotAvailable ? (
                    <select
                      data-testid="prod-batch"
                      value={prodForm.batch}
                      onChange={(e) => setProdForm({ ...prodForm, batch: e.target.value })}
                      className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">— Select RM batch —</option>
                      {batches.map((b) => (
                        <option key={b.batch_code} value={b.batch_code}>
                          {b.batch_code} · {b.grade} · {b.thickness_mm}×{b.width_mm}mm · {b.remaining_qty}kg left
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      {prodForm.tool
                        ? (batches.length === 0
                          ? "No RM batches available matching this tool's grade/spec."
                          : "Marked as batch not available")
                        : "Select a tool to see matching batches"}
                    </div>
                  )}
                  <label className="flex items-center gap-2 mt-2 text-xs font-normal text-slate-600 cursor-pointer">
                    <input
                      data-testid="prod-batch-na"
                      type="checkbox"
                      checked={prodForm.batchNotAvailable}
                      onChange={(e) => setProdForm({ ...prodForm, batchNotAvailable: e.target.checked, batch: "" })}
                    />
                    Batch not available — admin will map RM later
                  </label>
                </label>
              )}

              <label className="block text-xs font-bold text-slate-700">
                <span className="inline-flex items-center gap-1"><Layers size={12} /> Sub-code</span>
                <input
                  data-testid="prod-subcode"
                  value={prodForm.subCode}
                  onChange={(e) => setProdForm({ ...prodForm, subCode: e.target.value.toUpperCase().slice(0, 3) })}
                  placeholder="A"
                  className="mt-1 w-full border-slate-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                />
              </label>
            </div>

            {opInfo && opInfo.is_first_op === false && (
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-900">
                <b>Op {opInfo.op_seq} of {opInfo.total_ops}</b> — pulls from WIP (no RM batch needed).
                {opInfo.wip_available != null && ` WIP available: ${opInfo.wip_available} pcs.`}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-6">
              <button
                onClick={() => setMode(null)}
                className="text-sm font-bold text-slate-600 px-4 py-2 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                data-testid="prod-submit"
                onClick={handleStartProduction}
                disabled={
                  submitting ||
                  !prodForm.operator ||
                  !prodForm.tool ||
                  (opInfo?.is_first_op !== false && !prodForm.batch && !prodForm.batchNotAvailable)
                }
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-extrabold text-sm px-5 py-2 rounded-lg inline-flex items-center gap-2"
              >
                {submitting ? <Loader className="animate-spin" size={14} /> : <PlayCircle size={14} />}
                {submitting ? "Starting…" : "Start Production"}
              </button>
            </div>
          </div>
        )}

        {/* Recent setup sessions (last 5) */}
        {ctx?.recent_sessions?.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">
              Recent setup sessions on {machineCode}
            </p>
            <div className="space-y-1.5">
              {ctx.recent_sessions.map((s) => (
                <div key={s.session_id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-lg hover:bg-slate-50">
                  <div>
                    <b className="text-slate-900">{s.strokes} strokes</b>{" "}
                    <span className="text-slate-500">· {s.reason_label}</span>{" "}
                    {s.remark && <span className="text-slate-400 italic">· &quot;{s.remark}&quot;</span>}
                  </div>
                  <div className="text-slate-400 font-mono">
                    {new Date(s.logged_at).toLocaleString("en-IN")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassifyStrokes;
